import { IRowData } from "@multiweb/types/contracts";
import { Key } from "Global/Base/Utils/EventUtils";
import { MoveRecordDirection } from "../../../../UI.Controls/DataGrid";
import { CheckedDataRow } from "../../../../UI.Controls/DataGridSubcontrols/Components/CheckedDataRow";
import { IDataGridMultipleSelectionChangedEventData, IDataGridSelectionChangedEvent } from "../../../../UI.Controls/IEvent";
import { registerCustomElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { MSLOVGrid } from "./MSLOVGrid";
/**
 * Datagrid, který obsahuje záznamy, které je možné zaškrtnutím přidat do výběru.
 * Oproti předkovi MSLOVGrid je třeba rozlišit kolekci Selected a Checked. Na předkovi MSLOVGrid jde o totožnou kolekci. Tj. pokud se označí více záznamů (přes shift nebo ctrl), rovnou patří do výběru.
 * V případe MSLOVCheckedGridu se může jednat o rozdílné kolekci. Označené řádky se přidají mezi vybrané záznamy až stiskem mezerníku.
 */
export class MSLOVCheckedGrid extends MSLOVGrid<CheckedDataRow> {
    public isShowed: boolean;
    private _checkedItems: Set<CheckedDataRow>;
    private _selectedItems: Set<CheckedDataRow>;

    public get checkedItems(): IterableIterator<CheckedDataRow> {
        return this._checkedItems.values();
    }

    protected override _constructor(): void {
        super._constructor();

        this._checkedItems = new Set<CheckedDataRow>();
        this._selectedItems = new Set<CheckedDataRow>();
        this.fetchCount = 15;

        this.classList.add("CheckedDataGrid");
        //this.mainList_div.className = "compactListDiv";
        this.dataRows = new Array<CheckedDataRow>();
        this.selectedRows = new Array<CheckedDataRow>();
        this.headerRow.visible = false;

        this._dataRowFactory = () => new CheckedDataRow();

        this._eventsController.addListener(this, "SelectionChanged", (e: IDataGridSelectionChangedEvent) => {
            if (e.detail && e.detail.data && e.detail.data.keepSelectedRow)
                return;
            this.clearRowSelection();
        });
    }

    protected override async _attachDataGrid(): Promise<any> {
        await super._attachDataGrid();
    }

    protected override _createRow(data: IRowData): CheckedDataRow {
        if (data.ID.valueOf() == 0)
            return null;

        const dataRow = this._dataRowFactory(this, data);
        dataRow.init(data, this, this._createRowControlElement(dataRow));
        this.initDataRowFlag(dataRow, data);

        dataRow.afterAttach(() => {
            //if (this.isSelectedAll) dataRow.isSelected = true;
            this._eventsController.addListener(dataRow, "checkedChange", (e: CustomEvent) => {
                const el = e.detail.item as CheckedDataRow;

                if (el.isChecked)
                    this._checkedItems.add(el);
                else {
                    for (const si of this._checkedItems) {
                        if (si.dataKey == el.dataKey) {
                            this._checkedItems.delete(si);
                            break;
                        }
                    }
                }
            });
        });

        this.dataRows.push(dataRow);

        return dataRow;
    }

    protected override _createPreselectRow(data: IRowData): CheckedDataRow {
        const exist = (this.dataRows as CheckedDataRow[]).filter((item) => item.dataKey == data.ID.valueOf());
        if (exist.length > 0) {
            exist[0].isSelected = true;
            return exist[0];
        }

        const row = this._createRow(data);
        if (row == null)
            return null;
        row.afterAttach(() => {
            const isDisabled = row.isDisabled; // zde je to z toho důvodu, že potřebuji nastavit disabled na řádku který má být selected, ale selected se nedá nastavit pokud je disabled, proto taková klička
            row.isDisabled = false;
            row.isSelected = true;
            row.isDisabled = isDisabled;
            this._checkedItems.add(row);
        });
        return row;
    }

    public override insertExistingRecord(dataRow: IRowData): CheckedDataRow {
        return this.insertNewRecord(dataRow);
    }

    public override insertNewRecord(dataRow: IRowData): CheckedDataRow {
        const beforeRow = this.selectedRows.length > 0 && this.dataRows && this.dataRows.length > 0 ? (this.dataRows.indexOf(this.selectedRows[0]) > -1 ? this.selectedRows[0] : undefined) : (this.dataRows.length > 0 ? this.dataRows[0] : undefined);
        const newRow = this._dataRowFactory(this, dataRow);
        const dataSource = this.dataSource;
        newRow.init(dataRow, this, this._createRowControlElement(newRow));
        newRow.render();
        newRow.dataKey = dataRow[dataSource.dataKey].valueOf();

        this.emptyMessage.visible = false;

        if (beforeRow)
            this._dataTable.insertBefore(newRow as HTMLElement, beforeRow as HTMLElement);
        else
            this._dataTable.appendChild(newRow as HTMLElement);

        if (beforeRow) {
            const addIndex = this.dataRows.indexOf(beforeRow);
            this.dataRows.splice(addIndex, 0, newRow); //.push(newRow);
        } else {
            this.dataRows.push(newRow);
        }

        return newRow;
    }

    /**
     * Udrzeni vybranych radku pri refresh/razeni seznamu
     */
    public override async keepSelectedRows(): Promise<void> {
        this._concatenateLastSelectedDataKeys(this.selectedRows);
        super.keepSelectedRows();
    }

    /**
   * Posune aktualni zaznam
   */
    protected override _moveRecord(direction: MoveRecordDirection, trigger = true): void {
        let index: number = -1;
        if (this.lastClickedRow != null)
            index = this.keyPressRowIndex;

        if (direction == MoveRecordDirection.next && index + 1 >= this.dataRows.length)
            return; //stojim na poslednim zaznamu
        else if (direction == MoveRecordDirection.prev && index <= 0)
            return; //stojim na prvnim

        this.clearRowSelection();
        this.dataRows.forEach(r => r.classList.remove("selectedRecord"));
        this.lastClickedRow = this.dataRows[index + direction];
        this.lastClickedRow.classList.add("selectedRecord");
        this._selectedItems.add(this.lastClickedRow);
        if (this.lastClickedRow)
            this._adjustScrollTop(direction);
        if (trigger)
            this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey, true);
    }

    /**
     * Posun na prvni radek
     */
    public override homePrevRecord(): void {
        if (this.dataRows.length == 0) {
            return;
        }

        this.clearRowSelection();
        this.dataRows.forEach(r => r.classList.remove("selectedRecord"));
        this.lastClickedRow = this.dataRows[0];
        this.lastClickedRow.classList.add("selectedRecord");
        this._listDiv.scrollTop = 0;
    }

    /**
     * Oznacovani pomoci shift + home.
     */
    public override shiftSelectHomeRecord(): void {
        if (this.dataRows.length == 0) {
            return;
        }

        const index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (this.lastClickedRow != null)
            this.lastClickedRow.classList.remove("selectedRecord");

        for (let i = index; i >= 0; i--) {
            this.dataRows[i].isSelected = true;
            this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
        }

        this.lastClickedRow = this.dataRows[0];
        this.lastClickedRow.classList.add("selectedRecord");
        this._listDiv.scrollTop = 0;
    }

    /**
     * Posun na posledni nacteny zaznam
     */
    public override endNextRecord(): void {
        if (this.dataRows.length == 0) {
            return;
        }

        this.clearRowSelection();
        this.dataRows.forEach(r => r.classList.remove("selectedRecord"));
        this.lastClickedRow = this.dataRows[this.dataRows.length - 1];
        this.lastClickedRow.classList.add("selectedRecord");
        this._listDiv.scrollTop = this._listDiv.scrollHeight;
    }

    /**
     * Oznacovani pomoci shift + end.
     */
    public override shiftSelectEndRecord(): void {
        if (this.dataRows.length == 0) {
            return;
        }

        if (this.lastClickedRow != null) {
            this.lastClickedRow.classList.remove("selectedRecord");
        }

        let index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index < 0) {
            index = 0;
        }

        for (let i = index; i < this.dataRows.length; i++) {
            this.dataRows[i].isSelected = true;
            this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
        }

        this.lastClickedRow = this.dataRows[this.dataRows.length - 1];
        this.lastClickedRow.classList.add("selectedRecord");
        this._listDiv.scrollTop = this._listDiv.scrollHeight;
    }

    /**
     * Posun o stranku nahoru
     */
    public override pageUpPrevRecord(): void {
        if (this.dataRows.length == 0) {
            return;
        }

        const index: number = this.dataRows.indexOf(this.lastClickedRow);
        let row_cnt_next = index - Math.ceil(this._listDiv.clientHeight / (this.dataRows[0].clientHeight + 1));
        if (row_cnt_next < 0)
            row_cnt_next = 0;

        this.clearRowSelection();
        this.dataRows.forEach(r => r.classList.remove("selectedRecord"));
        this.lastClickedRow = this.dataRows[row_cnt_next];
        this.lastClickedRow.classList.add("selectedRecord");
        this._listDiv.scrollTop = (this.lastClickedRow.clientHeight + 1) * (row_cnt_next);
    }

    /**
     * Oznaceni zaznamu se shiftem a posun o stranku nahoru
     */
    public override shiftPageUpPrevRecord(): void {
        if (this.dataRows.length == 0) {
            return;
        }

        const index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (this.lastClickedRow != null)
            this.lastClickedRow.classList.remove("selectedRecord");

        let row_cnt_next = index - Math.ceil(this._listDiv.clientHeight / (this.dataRows[0].clientHeight + 1));
        if (row_cnt_next < 0)
            row_cnt_next = 0;

        for (let i = index; i >= row_cnt_next; i--) {
            this.dataRows[i].isSelected = true;
            this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
        }

        this.lastClickedRow = this.dataRows[row_cnt_next];
        this.lastClickedRow.classList.add("selectedRecord");
        this._listDiv.scrollTop = (this.lastClickedRow.clientHeight + 1) * (row_cnt_next);

        this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
    }

    /**
     * Posun o stranku dolu
     */
    public override pageDownNextRecord(): void {
        if (this.dataRows.length == 0) {
            return;
        }

        const index: number = this.dataRows.indexOf(this.lastClickedRow);
        let row_cnt_next = Math.ceil(this._listDiv.clientHeight / (this.dataRows[0].clientHeight + 1)) + index;
        if (this.dataRows.length <= row_cnt_next)
            row_cnt_next = this.dataRows.length - 1;

        this.clearRowSelection();
        this.dataRows.forEach(r => r.classList.remove("selectedRecord"));
        this.lastClickedRow = this.dataRows[row_cnt_next];
        this.lastClickedRow.classList.add("selectedRecord");
        this._listDiv.scrollTop = (this.lastClickedRow.clientHeight + 1) * (row_cnt_next) - this._listDiv.clientHeight + (this.lastClickedRow.clientHeight + 1);
    }

    /**
     * Oznaceni zaznamu se shiftem a posun o stranku dolu
     */
    public override shiftPageDownNextRecord(): void {
        if (this.dataRows.length == 0) {
            return;
        }

        let index: number = this.dataRows.indexOf(this.lastClickedRow);

        let row_cnt_next = Math.ceil(this._listDiv.clientHeight / (this.dataRows[0].clientHeight + 1)) + index;
        if (this.dataRows.length <= row_cnt_next)
            row_cnt_next = this.dataRows.length - 1;

        if (this.lastClickedRow != null) {
            this.lastClickedRow.classList.remove("selectedRecord");
        }

        if (index < 0) {
            index = 0;
        }

        for (let i = index; i <= row_cnt_next; i++) {
            this.dataRows[i].isSelected = true;
            this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
        }

        this.lastClickedRow = this.dataRows[row_cnt_next];
        this.lastClickedRow.classList.add("selectedRecord");
        this._listDiv.scrollTop = (this.lastClickedRow.clientHeight + 1) * (row_cnt_next) - this._listDiv.clientHeight + (this.lastClickedRow.clientHeight + 1);

        this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
    }

    /**
     * Smaže záznamy, které jsou zaškrtnuté.
     * V předkovi se kolekce nazývá selectedItems, v případě checkedDataGridu se jedná o kolekci checkedItem. SelectedItems představuje kolekci záznamů, které jsou vybraný například přes shift+click, ale nemusí být zaškrtnuty.
     */
    public override clearSelectedRows(): void {
        super.clearSelectedRows();
    }

    /**
    * Smaže označené záznamy. Jedná se o záznamy označené do výběru přes shift nebo crtr. Nejedná se o kolekci zaznamů, které jsou "checked".
    */
    public clearRowSelection(): void {
        if (this._selectedItems) {
            this._selectedItems.forEach(r => r.classList.remove("selectedRecord"));
        }
        this._selectedItems.clear();
    }

    /**
     * Vyber zaznamu od posledniho kliknuteho po posledni index (pri vyberu zaznamu Shift + Up/Down)
     * @param key - Kod klavesy - sipka nahoru nebo dolu
     */
    public override shiftSelectRecords(key: string = undefined): void {
        this.isSelectedAll = false;
        // mam uz oznaceny radek (radky)
        if (this.lastClickedRow) {
            let fromIndex = this.dataRows.indexOf(this.lastClickedRow); // vezmu naposledy kliknuty radek
            let selectAnotherItem = true;
            let skipSelecting = false;
            let selectTwoItemsDirection = 0; // 0 - není tento případ, 1 - situace při šipce nahoru, -1 - situace při šipce dolů

            if (key === Key.ArrowUp) { // up
                if (this.keyPressRowIndex == -1) { // mimo rozsah
                    this.keyPressRowIndex++;
                    return;
                }
                if (!this.dataRows[this.keyPressRowIndex + 1].isSelected && !this.dataRows[this.keyPressRowIndex].isSelected) { // pokud jsem na neoznačeném záznamu a se shiftem se posunu na další, tak musím označit i ten první
                    selectTwoItemsDirection = 1;
                } else if (this.dataRows[this.keyPressRowIndex].isSelected && this.dataRows[this.keyPressRowIndex + 1].isSelected && fromIndex > (this.keyPressRowIndex)) { // šipkou nahoru se dostanu na označený záznam a zároveň se nejedná o "undo"
                    skipSelecting = true;
                } else if (this.dataRows[this.keyPressRowIndex].isSelected) {
                    selectAnotherItem = false;
                    this.keyPressRowIndex++;
                }
            } else if (key === Key.ArrowDown) { // down
                if (this.keyPressRowIndex == this.dataRows.length) {
                    this.keyPressRowIndex--;
                    return;
                }
                if (!this.dataRows[this.keyPressRowIndex - 1].isSelected && !this.dataRows[this.keyPressRowIndex].isSelected) {
                    selectTwoItemsDirection = -1;
                } else if (this.dataRows[this.keyPressRowIndex].isSelected && this.dataRows[this.keyPressRowIndex - 1].isSelected && fromIndex < (this.keyPressRowIndex)) {
                    skipSelecting = true;
                } else if (this.dataRows[this.keyPressRowIndex].isSelected) {
                    selectAnotherItem = false;
                    this.keyPressRowIndex--;
                }
            }

            if (this.lastClickedRow != null)
                this.lastClickedRow.classList.remove("selectedRecord");

            let toIndex = this.keyPressRowIndex; // index aktualniho
            let direction = MoveRecordDirection.next;
            if (fromIndex > toIndex) { //v pripade ze index prvniho je vetsi, tak prohodime
                direction = MoveRecordDirection.prev;
                const tmp = fromIndex;
                fromIndex = toIndex;
                toIndex = tmp;
            }
            if (fromIndex < 0) {
                this.keyPressRowIndex = fromIndex = 0;
            }
            if (toIndex >= this.dataRows.length)
                this.keyPressRowIndex = toIndex = this.dataRows.length - 1;

            this.clearRowSelection(); // vymazeme oznacene radky
            this._adjustScrollTop(direction, this.dataRows[direction === MoveRecordDirection.next ? toIndex : fromIndex]);

            if (!skipSelecting) {
                for (let i = fromIndex; i <= toIndex; i++) { // prochazime a oznacime radky od fromIndexu po toIndex
                    if (this.keyPressRowIndex == i || (this.keyPressRowIndex + selectTwoItemsDirection) == i) {
                        this.dataRows[i].isSelected = !this.dataRows[i].isSelected;
                    }
                    this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
                }
            }

            if (!selectAnotherItem) {
                if (key === Key.ArrowUp) {
                    this.keyPressRowIndex--;
                } else if (key === Key.ArrowDown) {
                    this.keyPressRowIndex++;
                }
            }

            this.dataRows[this.keyPressRowIndex].classList.add("selectedRecord");
            this._selectedItems.add(this.dataRows[this.keyPressRowIndex]);
        }
    }

    /**
     * Provede označení/odznačení aktuální výběru řádků
     */
    public override toggleRowSelection(): void {
        if (this._selectedItems.size > 0) {
            const selectedRows = Array.from(this._selectedItems);
            //pokud alespon jeden řádek ve výběru není označen, provede označení všech na true.
            const newSelectedValue = selectedRows.some(r => !r.isSelected);
            this._selectedItems.forEach(r => r.isSelected = newSelectedValue);
            this._dispatchMultipleSelectionChangeEvent(selectedRows.map(r => r.dataKey), { doubleClick: false });
        } else if (this._lastClickedRow) { //stojím na aktuálním záznamu
            this._lastClickedRow.isSelected = !this._lastClickedRow.isSelected;
            this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey, true);
        }
    }

    /**
     * Spusteni SelectionChanged po 300ms od posledniho volani. Pouziva se pri oznacovani novych .
     * Vlozeni timeoutu, z duvodu zbytecne prodlevy a zateze serveru mezi rychlím preklikavanim nad jednotlivymi zaznamy
     * @param rowID - Id záznamu
     * @param keepSelectedRow - pokud je nastaveno na true, nechá označený aktuálně vybraný záznam.
     */
    public override triggerSelectionChangedAfterTimeout(rowID: string, keepSelectedRow = false): void {
        if (this.getSelectedRowInfoTimer)
            clearTimeout(this.getSelectedRowInfoTimer);
        this.getSelectedRowInfoTimer = self.setTimeout(() => {
            this._dispatchSelectionChangeEvent(rowID, { doubleClick: false, clickedRow: this.lastClickedRow, keepSelectedRow: keepSelectedRow });
        }, 300);
    }

    /**
     * Událost, která se vyvolá v případě že došlo k hromadné změně vybraných záznamů. (V tomto případě došlo ke změně isChecked nad více záznamy)
     */
    protected _dispatchMultipleSelectionChangeEvent(rowIDs: string[], data: any): void {
        this.callEvent("MultipleSelectionChanged", { dataGrid: this, rowIDs: rowIDs, data: data } as IDataGridMultipleSelectionChangedEventData, true, true);
    }
}

registerCustomElement("tsc-MSLovCheckedDataGrid", MSLOVCheckedGrid);
